import React from 'react';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconButton, Tooltip } from '@mui/joy';
import { Chat } from '@mui/icons-material';
import { useDispatch } from 'react-redux';

// Vertical anchor for both the launcher icon and the chat panel, as a
// fraction down the viewport (2/3, per request) rather than flush to the
// bottom - kept in one place so the two stay aligned.
const ANCHOR_FROM_TOP_VH = 66.67;
const ANCHOR_FROM_BOTTOM_VH = 100 - ANCHOR_FROM_TOP_VH;
// Comfortably above every z-index MUI Joy uses internally (modals, popups,
// tooltips all top out well under 2000) so the launcher and chat always
// render in front of the rest of the app.
const FRONT_Z_INDEX = 999999;

// Drag feel: the panel doesn't snap straight to the pointer, it eases toward
// it every frame (lower = laggier/heavier), and on release keeps coasting
// with its own inertia and drag before easing into whichever side it's
// closer to.
const DRAG_EASE = 0.16;
const RELEASE_FRICTION = 0.94;
const EDGE_MARGIN = 16;

// Keys that identify a DecaySelector state document (see SaveButton's getState
// and the chat's own exporter) - used to avoid hydrating from an unrelated
// JSON blob if something else on the page happens to create one.
const DECAY_SELECTOR_KEYS = ['decay', 'finalState', 'intermediateState'];

function looksLikeDecaySelectorDoc(jsonData) {
    return jsonData && typeof jsonData === 'object'
        && DECAY_SELECTOR_KEYS.every((key) => key in jsonData);
}

export default function ChatWidget() {
    const dispatch = useDispatch();

    const hydrate = (jsonString) => {
        try {
            const jsonData = JSON.parse(jsonString);
            if (!looksLikeDecaySelectorDoc(jsonData)) return false;
            dispatch({ type: 'HYDRATE', payload: jsonData });
            return true;
        } catch (error) {
            console.error('PWAgentChat produced invalid JSON:', error);
            return false;
        }
    };

    // The chat is rendered into an open shadow root (#pwagent-chat-host), so we
    // can reach into it to fix up styling the embed itself doesn't set: it
    // ships as a full-screen, centered, dark-themed modal. We want a small
    // panel anchored to the corner it launches from instead, with a light
    // background - both are only exposed as CSS, not as options to open(), so
    // we override them by injecting our own stylesheet into the shadow root.
    const fixOverlayStyle = () => {
        const shadow = document.getElementById('pwagent-chat-host')?.shadowRoot;
        if (!shadow || shadow.getElementById('pwagent-chat-zfix')) return;
        const style = document.createElement('style');
        style.id = 'pwagent-chat-zfix';
        style.textContent = `
            .pwagent-embed-overlay {
                z-index: ${FRONT_Z_INDEX} !important;
                position: fixed !important;
                inset: auto !important;
                top: auto !important;
                left: auto !important;
                left: 16px !important;
                bottom: calc(${ANCHOR_FROM_BOTTOM_VH}vh + 40px) !important;
                display: block !important;
                width: auto !important;
                height: auto !important;
                padding: 0 !important;
                background: transparent !important;
            }
            :host {
                --ink: #202226;
                --panel: #2b2d33;
                --raise: #3a3d44;
                --text: #eceef2;
                --muted: #a3a8b4;
                --line: #4a4d55;
            }
            .opt.primary {
                background: var(--raise) !important;
                border-color: #8a6bff !important;
                color: #b39dff !important;
                text-shadow: 0 0 8px rgba(138, 107, 255, 0.9), 0 0 16px rgba(138, 107, 255, 0.5) !important;
            }
            .opt.primary:hover {
                border-color: #a98bff !important;
                text-shadow: 0 0 10px rgba(179, 157, 255, 1), 0 0 22px rgba(179, 157, 255, 0.7) !important;
            }
        `;
        shadow.appendChild(style);
    };

    // The chat's "Download JSON" button builds the file via
    // `URL.createObjectURL(new Blob([json], {type: 'application/json'}))` and
    // clicks a throwaway <a download>, so there's no DOM node or event we can
    // listen for. Instead, intercept object-URL creation for the duration the
    // chat is open and pipe any JSON blob straight into the upload/HYDRATE
    // flow instead of only letting it fall to disk. This is a backup for the
    // `onComplete` callback below, which already fires the same JSON the
    // moment the chat reaches "done" - normally you shouldn't need to touch
    // Download at all.
    const interceptDownload = () => {
        const host = document.getElementById('pwagent-chat-host');
        if (!host || host.dataset.downloadHooked) return;
        host.dataset.downloadHooked = '1';

        const originalCreateObjectURL = URL.createObjectURL.bind(URL);
        URL.createObjectURL = (blob) => {
            if (blob instanceof Blob && blob.type === 'application/json') {
                blob.text().then((text) => {
                    if (hydrate(text)) window.PWAgentChat.close();
                });
            }
            return originalCreateObjectURL(blob);
        };

        const observer = new MutationObserver(() => {
            if (host.style.display === 'none') {
                URL.createObjectURL = originalCreateObjectURL;
                delete host.dataset.downloadHooked;
                observer.disconnect();
            }
        });
        observer.observe(host, { attributes: true, attributeFilter: ['style'] });
    };

    const [chatOpen, setChatOpen] = useState(false);

    // Launcher-icon dragging: position is tracked in refs (mutated every
    // animation frame) and mirrored into React state only for rendering.
    // The icon eases toward the pointer rather than tracking it 1:1 (a
    // heavier, slightly-lagging feel), and on release keeps coasting on its
    // recent velocity before easing into whichever side of the screen it
    // ends up closer to.
    const launcherRef = useRef(null);
    const dragStateRef = useRef(null);
    const [launcherPos, setLauncherPos] = useState(null);

    useEffect(() => {
        const button = launcherRef.current;
        if (!button) return undefined;

        const rect = button.getBoundingClientRect();
        const state = {
            x: rect.left,
            y: rect.top,
            targetX: rect.left,
            targetY: rect.top,
            vx: 0,
            vy: 0,
            dragging: false,
            moved: false,
            pointerId: null,
            offsetX: 0,
            offsetY: 0,
            lastMoveTime: 0,
            lastMoveX: 0,
            lastMoveY: 0,
            rafId: null,
        };
        dragStateRef.current = state;

        const clampX = (x) => Math.min(Math.max(x, EDGE_MARGIN), window.innerWidth - button.offsetWidth - EDGE_MARGIN);
        const clampY = (y) => Math.min(Math.max(y, EDGE_MARGIN), window.innerHeight - button.offsetHeight - EDGE_MARGIN);

        const settleToNearestSide = () => {
            const midX = state.x + button.offsetWidth / 2;
            const snapLeft = midX < window.innerWidth / 2;
            state.targetX = clampX(snapLeft ? EDGE_MARGIN : window.innerWidth - button.offsetWidth - EDGE_MARGIN);
            state.targetY = clampY(state.y);
        };

        const tick = () => {
            if (!state.dragging) {
                if (Math.abs(state.vx) > 0.02 || Math.abs(state.vy) > 0.02) {
                    // Coast on release momentum, bleeding it off each frame.
                    state.targetX = clampX(state.targetX + state.vx);
                    state.targetY = clampY(state.targetY + state.vy);
                    state.vx *= RELEASE_FRICTION;
                    state.vy *= RELEASE_FRICTION;
                    if (Math.abs(state.vx) <= 0.02 && Math.abs(state.vy) <= 0.02) settleToNearestSide();
                }
            }

            const settled = Math.abs(state.targetX - state.x) < 0.05 && Math.abs(state.targetY - state.y) < 0.05;
            if (!settled) {
                state.x += (state.targetX - state.x) * DRAG_EASE;
                state.y += (state.targetY - state.y) * DRAG_EASE;
                setLauncherPos({ left: state.x, top: state.y });
            }

            state.rafId = requestAnimationFrame(tick);
        };
        state.rafId = requestAnimationFrame(tick);

        const onPointerMove = (event) => {
            if (state.pointerId !== event.pointerId) return;
            const now = performance.now();
            const dt = Math.max(now - state.lastMoveTime, 1);
            state.vx = ((event.clientX - state.lastMoveX) / dt) * 16;
            state.vy = ((event.clientY - state.lastMoveY) / dt) * 16;
            state.lastMoveTime = now;
            state.lastMoveX = event.clientX;
            state.lastMoveY = event.clientY;
            state.moved = true;

            state.targetX = clampX(event.clientX - state.offsetX);
            state.targetY = clampY(event.clientY - state.offsetY);
        };

        const onPointerUp = (event) => {
            if (state.pointerId !== event.pointerId) return;
            state.dragging = false;
            state.pointerId = null;
            button.style.cursor = 'grab';
            button.releasePointerCapture?.(event.pointerId);
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
            if (Math.abs(state.vx) <= 0.02 && Math.abs(state.vy) <= 0.02) settleToNearestSide();
        };

        const onPointerDown = (event) => {
            if (event.button !== undefined && event.button !== 0) return;
            state.dragging = true;
            state.moved = false;
            state.pointerId = event.pointerId;
            button.style.cursor = 'grabbing';
            const box = button.getBoundingClientRect();
            state.offsetX = event.clientX - box.left;
            state.offsetY = event.clientY - box.top;
            state.vx = 0;
            state.vy = 0;
            state.lastMoveTime = performance.now();
            state.lastMoveX = event.clientX;
            state.lastMoveY = event.clientY;
            document.addEventListener('pointermove', onPointerMove);
            document.addEventListener('pointerup', onPointerUp);
        };

        button.style.cursor = 'grab';
        button.addEventListener('pointerdown', onPointerDown);

        const onResize = () => {
            state.targetX = clampX(state.targetX);
            state.targetY = clampY(state.targetY);
        };
        window.addEventListener('resize', onResize);

        return () => {
            cancelAnimationFrame(state.rafId);
            button.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const handleOpenToggle = () => {
        // Suppress the click that fires right after a drag - onClick still
        // runs on pointerup even when the pointer travelled, so without
        // this a drag would also toggle the chat open/closed.
        if (dragStateRef.current?.moved) {
            dragStateRef.current.moved = false;
            return;
        }

        if (!window.PWAgentChat) {
            console.error('PWAgentChat is not available (script failed to load).');
            return;
        }

        if (chatOpen){
            window.PWAgentChat.close();
            setChatOpen(false);
            return
        }

        window.PWAgentChat.open({
            onComplete: (decaySelectorJson) => hydrate(decaySelectorJson),
        });
        // open() creates the host/shadow root and renders synchronously.
        fixOverlayStyle();
        interceptDownload();
        setChatOpen(true);
    };

    // Rendered via a portal straight into <body>, as a sibling of the app's
    // own #root rather than a descendant of the main Grid - so it's never
    // subject to that layout's sizing/overflow/stacking, only the viewport.
    return ReactDOM.createPortal(
        <Tooltip title="Build a model with the chat assistant" placement="right">
            <IconButton
                ref={launcherRef}
                variant="solid"
                color="primary"
                onClick={handleOpenToggle}
                sx={{
                    position: 'fixed',
                    left: launcherPos ? `${launcherPos.left}px` : 20,
                    top: launcherPos ? `${launcherPos.top}px` : `${ANCHOR_FROM_TOP_VH}vh`,
                    transform: launcherPos ? 'none' : 'translateY(-50%)',
                    touchAction: 'none',
                    zIndex: FRONT_Z_INDEX,
                    borderRadius: '50%',
                    width: 52,
                    height: 52,
                    border: '2px solid rgba(255, 255, 255, 0.85)',
                    boxShadow:
                        '0 0 0 3px rgba(88, 193, 232, 0.28), 0 0 18px 4px rgba(88, 193, 232, 0.55), 0 4px 14px rgba(0, 0, 0, 0.35)',
                    transition: 'box-shadow 150ms ease-out, transform 150ms ease-out',
                    '&:hover': {
                        transform: launcherPos ? 'scale(1.06)' : 'translateY(-50%) scale(1.06)',
                        boxShadow:
                            '0 0 0 3px rgba(88, 193, 232, 0.4), 0 0 26px 6px rgba(88, 193, 232, 0.75), 0 4px 14px rgba(0, 0, 0, 0.4)',
                    },
                }}
            >
                <Chat />
            </IconButton>
        </Tooltip>,
        document.body
    );
}
